const BRANCH_RETURN_KEY = 'psBranchReturnUrl'
const BRANCH_PREFIX_RE = /^\/_branch\/[^/]+/

/**
 * Returns the `/_branch/{name}` prefix of the current URL when running on a
 * branch deploy, or null on a canonical deploy.
 */
export function getBranchPrefix(): string | null {
  const match = window.location.pathname.match(BRANCH_PREFIX_RE)
  return match ? match[0] : null
}

/**
 * Carry the current branch-deploy prefix into a same-origin, root-relative path.
 *
 * ONLY for raw `window.location.*` navigations — those bypass the router, so they're
 * the ones that drop the prefix. Never combine this with `history.push`/`<Link to>`/
 * `<Redirect to>`: the history basename is already the branch prefix (see ./history.ts),
 * so you'd get `/_branch/{name}/_branch/{name}/…`. On a canonical deploy the prefix is
 * empty and the path is returned unchanged.
 */
export function withBranchPath(path: string): string {
  return `${getBranchPrefix() ?? ''}${path}`
}

/**
 * Build an absolute URL into another PaintScout app, carrying over the current
 * branch-deploy prefix so cross-app links (e.g. admin → app masquerade) stay on
 * the same branch. Both apps deploy each branch under the same `/_branch/{name}`
 * prefix, so the prefix read from the current URL is valid for `appUrl` too. On a
 * canonical deploy the prefix is empty, so it's just `${appUrl}${path}`.
 */
export function withBranchPrefix(appUrl: string, path = ''): string {
  // strip a trailing slash off appUrl so a caller passing `https://app.x.com/`
  // can't produce a double slash before the prefix/path
  return `${appUrl.replace(/\/$/, '')}${withBranchPath(path)}`
}

/**
 * Call before an Auth0 logout redirect. Auth0's allowed logout URLs only
 * whitelist canonical origins (no `/_branch/` paths), so logout always lands
 * on the canonical app — stash the branch prefix in sessionStorage (per-tab,
 * survives the Auth0 round trip, expires with the tab) so the next login can
 * return to the branch deploy. No-op on canonical deploys.
 */
export function storeBranchReturnUrl(): void {
  const prefix = getBranchPrefix()
  if (prefix) {
    sessionStorage.setItem(BRANCH_RETURN_KEY, `${prefix}/`)
  }
}

/**
 * Resolves where a successful login callback on the canonical app should
 * hard-redirect to load a branch deploy, consuming any return URL stashed by
 * storeBranchReturnUrl. An explicit `/_branch/` previousUrl (captured by the
 * Authorizer before the login redirect) wins over the stashed value. Returns
 * null when no branch redirect applies — callers fall through to normal
 * routing. Always read-and-clears the stash so a stale or malformed value can
 * never affect a later login in the same tab.
 */
export function consumeBranchRedirectUrl(previousUrl?: string | null): string | null {
  const stored = sessionStorage.getItem(BRANCH_RETURN_KEY)
  if (stored) {
    sessionStorage.removeItem(BRANCH_RETURN_KEY)
  }

  const redirectUrl = previousUrl && BRANCH_PREFIX_RE.test(previousUrl) ? previousUrl : stored

  // don't trust the stored string blindly — only redirect to branch-deploy paths
  if (redirectUrl && BRANCH_PREFIX_RE.test(redirectUrl)) {
    console.info(`[branchDeploy] redirecting post-login to branch deploy: ${redirectUrl}`)
    return redirectUrl
  }

  return null
}
